import { getRouteForGoogleRole } from "./completeGoogleAuth";
import { shouldShowGuestProfileForm } from "./guestProfileForm";

export type PostAuthDestination = {
  path: string;
  state?: any;
};

const AUTH_PATH_PREFIXES = ["/sign-in", "/get-started"];

export function isAuthPagePath(pathname: string): boolean {
  const path = pathname.split("?")[0].replace(/\/+$/, "") || "/";
  return AUTH_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

function sanitizeRedirect(url: string | null): string | null {
  if (!url || !url.startsWith("/") || url.startsWith("//") || url.includes("\\")) return null;
  const path = url.split(/[?#]/)[0].replace(/\/+$/, "") || "/";
  if (isAuthPagePath(path) || path === "/") return null;
  return url;
}

export function consumePostLoginRedirect(): string | null {
  const url = localStorage.getItem("postLoginRedirect");
  if (url) localStorage.removeItem("postLoginRedirect");
  return sanitizeRedirect(url);
}

export function savePostLoginRedirect(url?: string) {
  const target = url ?? `${window.location.pathname}${window.location.search}`;
  const safeTarget = sanitizeRedirect(target);
  if (safeTarget) localStorage.setItem("postLoginRedirect", safeTarget);
}

export function saveReturnToProperty(slug?: string) {
  const propertyPath = `${window.location.pathname}${window.location.search}`;
  savePostLoginRedirect(propertyPath);
  sessionStorage.setItem(
    "pendingBooking",
    JSON.stringify({
      slug: slug || propertyPath.split("/")[2],
      propertyPath,
      returnTo: "property",
    }),
  );
}

export function persistProfileUser(profile: any) {
  if (profile) {
    sessionStorage.setItem("user", JSON.stringify(profile));
  }
}

export function resolvePostAuthDestination(
  profileUser?: any,
): PostAuthDestination {
  const pendingRaw = sessionStorage.getItem("pendingBooking");
  if (pendingRaw) {
    try {
      const pending = JSON.parse(pendingRaw);
      sessionStorage.removeItem("pendingBooking");
      const savedRedirect = consumePostLoginRedirect();

      if (pending.returnTo === "property") {
        const path =
          sanitizeRedirect(pending.propertyPath) ||
          (pending.slug ? sanitizeRedirect(`/property/${pending.slug}`) : null) ||
          savedRedirect;
        if (path) return { path };
      } else {
        const bookingData = {
          ...pending,
          showGuestProfile: shouldShowGuestProfileForm(
            profileUser || undefined,
          ),
        };
        if (pending.slug) {
          return {
            path: `/property/${pending.slug}/book`,
            state: bookingData,
          };
        }
        return { path: "/booking/new", state: bookingData };
      }
    } catch {
      sessionStorage.removeItem("pendingBooking");
    }
  }

  const redirectUrl = consumePostLoginRedirect();
  if (redirectUrl) return { path: redirectUrl };

  return { path: getRouteForGoogleRole(profileUser?.role) };
}

export function goToPostAuthDestination(
  navigate: (to: string, opts?: { replace?: boolean; state?: any }) => void,
  dest: PostAuthDestination,
) {
  const currentPath = window.location.pathname.replace(/\/+$/, "") || "/";
  const destPath = dest.path.split("?")[0].replace(/\/+$/, "") || "/";
  if (currentPath === destPath) {
    window.location.reload();
    return;
  }
  navigate(dest.path, { replace: true, state: dest.state });
}
